import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage } from '../services/openRouterService';
import './Chat.css';

interface ChatProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  onNewDocument: () => void;
}

const Chat: React.FC<ChatProps> = ({ messages, onSendMessage, isLoading, onNewDocument }) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Format message content with basic markdown support
  const formatMessageContent = (content: string) => {
    // Convert **text** to bold
    const boldText = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert line breaks to <br> tags
    const withLineBreaks = boldText.replace(/\n/g, '<br>');
    
    return withLineBreaks;
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>AI Assistant</h3>
        <button 
          className="new-document-btn"
          onClick={onNewDocument}
          disabled={isLoading}
        >
          New Document
        </button>
      </div>
      
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>Start a conversation to build your document!</p>
            <p>Try asking:</p>
            <ul>
              <li>"Create an AWS migration strategy for our legacy system"</li>
              <li>"Design a microservices architecture using AWS ECS"</li>
              <li>"Develop an AI/ML infrastructure proposal"</li>
            </ul>
            <div className="modification-tips">
              <p><strong>💡 Document Modification Tips:</strong></p>
              <ul>
                <li>"Add a section about cost optimization strategies"</li>
                <li>"Expand the security and compliance requirements"</li>
                <li>"Include more details about disaster recovery planning"</li>
                <li>"Add performance monitoring and observability sections"</li>
              </ul>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            // Determine message type for styling
            const isDocumentStructure = message.content.includes('Document Structure:');
            const isDeepResearch = message.content.includes('DeepResearch Mode');
            const isCompletion = message.content.includes('complete!');
            
            let messageClass = `message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`;
            if (isDocumentStructure) messageClass += ' document-structure-message';
            if (isDeepResearch) messageClass += ' deep-research-message';
            if (isCompletion) messageClass += ' completion-message';
            
            return (
              <div
                key={index}
                className={messageClass}
              >
                <div 
                  className="message-content"
                  dangerouslySetInnerHTML={{ 
                    __html: formatMessageContent(message.content) 
                  }}
                />
              </div>
            );
          })
        )}
        {isLoading && (
          <div className="message assistant-message">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={handleSubmit}>
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me to modify the document or ask questions..."
            disabled={isLoading}
            rows={1}
            className="chat-input"
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || isLoading}
            className="send-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};

export default Chat; 